// =====================================================
// GUEST REGISTRATION FORM
// Stage 2: Participants & Pricing
// Form for registering guest participants
// =====================================================

import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { MinisterSearchCombobox } from './MinisterSearchCombobox';

// ===================
// TYPES
// ===================

export interface GuestFormData {
  full_name: string;
  dni?: string;
  email?: string;
  phone?: string;
  age?: number;
  church_affiliation?: string;
  representing_minister_id?: string;
}

interface GuestRegistrationFormProps {
  eventId: string;
  value: GuestFormData;
  onChange: (data: GuestFormData) => void;
  errors?: Partial<Record<keyof GuestFormData, string>>;
}

// ===================
// COMPONENT
// ===================

export function GuestRegistrationForm({ eventId, value, onChange, errors }: GuestRegistrationFormProps) {
  const updateField = <K extends keyof GuestFormData>(field: K, val: GuestFormData[K]) => {
    onChange({ ...value, [field]: val });
  };

  return (
    <div className="space-y-4">
      {/* Full Name - Required */}
      <div className="space-y-2">
        <Label htmlFor="guest-full-name">
          Nombre Completo <span className="text-destructive">*</span>
        </Label>
        <Input
          id="guest-full-name"
          placeholder="Juan Pérez García"
          value={value.full_name}
          onChange={(e) => updateField('full_name', e.target.value)}
          className={errors?.full_name ? 'border-destructive' : ''}
        />
        {errors?.full_name && <p className="text-xs text-destructive">{errors.full_name}</p>}
      </div>

      {/* DNI - Optional */}
      <div className="space-y-2">
        <Label htmlFor="guest-dni">DNI</Label>
        <Input
          id="guest-dni"
          placeholder="12345678"
          value={value.dni || ''}
          onChange={(e) => updateField('dni', e.target.value)}
          className={errors?.dni ? 'border-destructive' : ''}
        />
        {errors?.dni && <p className="text-xs text-destructive">{errors.dni}</p>}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="guest-email">Email</Label>
          <Input
            id="guest-email"
            type="email"
            placeholder="correo@ejemplo.com"
            value={value.email || ''}
            onChange={(e) => updateField('email', e.target.value)}
            className={errors?.email ? 'border-destructive' : ''}
          />
          {errors?.email && <p className="text-xs text-destructive">{errors.email}</p>}
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <Label htmlFor="guest-phone">Teléfono</Label>
          <Input
            id="guest-phone"
            placeholder="+591 70123456"
            value={value.phone || ''}
            onChange={(e) => updateField('phone', e.target.value)}
            className={errors?.phone ? 'border-destructive' : ''}
          />
          {errors?.phone && <p className="text-xs text-destructive">{errors.phone}</p>}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {/* Age */}
        <div className="space-y-2">
          <Label htmlFor="guest-age">Edad</Label>
          <Input
            id="guest-age"
            type="number"
            placeholder="30"
            min="1"
            max="120"
            value={value.age || ''}
            onChange={(e) => updateField('age', e.target.value ? parseInt(e.target.value, 10) : undefined)}
            className={errors?.age ? 'border-destructive' : ''}
          />
          {errors?.age && <p className="text-xs text-destructive">{errors.age}</p>}
        </div>

        {/* Church Affiliation */}
        <div className="space-y-2">
          <Label htmlFor="guest-church">Iglesia/Organización</Label>
          <Input
            id="guest-church"
            placeholder="Iglesia Central"
            value={value.church_affiliation || ''}
            onChange={(e) => updateField('church_affiliation', e.target.value)}
            className={errors?.church_affiliation ? 'border-destructive' : ''}
          />
          {errors?.church_affiliation && (
            <p className="text-xs text-destructive">{errors.church_affiliation}</p>
          )}
        </div>
      </div>

      {/* Representing Minister */}
      <div className="space-y-2">
        <Label htmlFor="guest-representing">
          Representa a Ministro{' '}
          <span className="text-xs font-normal text-muted-foreground">(Opcional)</span>
        </Label>
        <MinisterSearchCombobox
          eventId={eventId}
          value={value.representing_minister_id}
          onSelect={(ministerId) => updateField('representing_minister_id', ministerId)}
          disabled={false}
        />
        <p className="text-xs text-muted-foreground">
          Si este invitado asiste en lugar de un ministro que no pudo venir, selecciónalo aquí.
          El ministro será contado como asistente en los reportes.
        </p>
      </div>

      <p className="text-xs text-muted-foreground">* Campos requeridos</p>
    </div>
  );
}
